import { v4 as uuid } from 'uuid';
import type {
  ArticleImportManifest,
  ArticleImportFile,
  ArticleImportResult,
  ArticleImportRules,
  SelectedArticleImportFile,
} from '@/axios/articleImport';

/**
 * 读取单个文件并计算 SHA-256，不同时载入整批文件。
 * @param {File} file 当前文件。
 * @returns {Promise<string>} 小写十六进制内容摘要。
 */
export async function hashImportFile(file: File): Promise<string> {
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('文件读取失败，请重新选择'));
    reader.onabort = () => reject(new Error('文件读取已取消'));
    reader.readAsArrayBuffer(file);
  });
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * 按选择顺序校验并添加文件，内容相同只保留先添加项。
 * @param {SelectedArticleImportFile[]} existing 已选文件。
 * @param {File[]} incoming 新选择的文件。
 * @param {ArticleImportRules} rules 服务端限制。
 * @param {(file: File) => Promise<string>} digest 顺序摘要计算函数。
 * @returns {Promise<{files: SelectedArticleImportFile[], rejected: string[]}>} 新清单及拒绝原因。
 */
export async function addImportFiles(
  existing: SelectedArticleImportFile[],
  incoming: File[],
  rules: ArticleImportRules,
  digest: (file: File) => Promise<string> = hashImportFile
): Promise<{ files: SelectedArticleImportFile[]; rejected: string[] }> {
  const files = [...existing];
  const rejected: string[] = [];
  const hashes = new Set(existing.map((item) => item.sha256));
  for (const file of incoming) {
    const extension = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!rules.supportedExtensions.includes(extension)) {
      rejected.push(file.name + '：不支持的文件格式');
      continue;
    }
    if (!file.size) {
      rejected.push(file.name + '：文件为空');
      continue;
    }
    if (file.size > rules.maxFileSizeBytes) {
      rejected.push(file.name + '：超过单文件大小限制');
      continue;
    }
    if (files.length >= rules.maxFileCount) {
      rejected.push(file.name + '：超出本批文件数量限制，未添加');
      continue;
    }
    try {
      const sha256 = await digest(file);
      if (hashes.has(sha256)) {
        rejected.push(file.name + '：文件内容重复，未添加');
        continue;
      }
      hashes.add(sha256);
      files.push({ fileId: uuid(), filename: file.name, size: file.size, sha256, file });
    } catch {
      rejected.push(file.name + '：文件摘要计算失败，请重新选择');
    }
  }
  return { files, rejected };
}

/**
 * 构造单次完整 multipart，仅清单身份进入 JSON。
 * @param {ArticleImportManifest} manifest 本次批次清单。
 * @param {SelectedArticleImportFile[]} files 本轮原文件。
 * @returns {FormData} 包含 application/json 清单和文件部分的请求。
 */
export function buildBatchFormData(
  manifest: ArticleImportManifest,
  files: SelectedArticleImportFile[]
): FormData {
  const body = new FormData();
  const identities = files.map(({ fileId, filename, size, sha256 }) => ({ fileId, filename, size, sha256 }));
  body.append('manifest', new Blob([JSON.stringify({ ...manifest, files: identities })], { type: 'application/json' }));
  files.forEach((entry) => body.append('file_' + entry.fileId, entry.file, entry.filename));
  return body;
}

/** 页面汇总模型；countsKnown 仅在浏览器内使用，不能发送为接口协议字段。 */
export interface ArticleImportViewResult extends Omit<ArticleImportResult, 'importedArticleCount' | 'skippedArticleCount'> {
  countsKnown: boolean;
  importedArticleCount?: number;
  skippedArticleCount?: number;
}

/**
 * 检查计数为可安全累加的非负整数。
 * @param {unknown} value 待检查值。
 * @returns {boolean} 是否为合法计数。
 */
function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/**
 * 检查同步结果覆盖本次文件且分类互斥；不接受部分响应或其他批次结果。
 * @param {unknown} value Axios 解包后的响应。
 * @param {ArticleImportManifest} manifest 本次提交清单。
 * @returns {boolean} 响应是否具有完整且一致的最终证据。
 */
function isCompleteImportResult(value: unknown, manifest: ArticleImportManifest): value is ArticleImportResult {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const result = value as ArticleImportResult;
  if (result.batchId !== manifest.batchId || !Array.isArray(result.successfulFiles)
    || !Array.isArray(result.failedFiles) || !Array.isArray(result.unknownFiles) || !Array.isArray(result.warningFiles)) {
    return false;
  }
  const expected = new Map(manifest.files.map((entry) => [entry.fileId, entry.filename]));
  const seen = new Set<string>();
  // 文件只允许归属一个最终分类，集合必须与本次提交清单完全相同。
  for (const entry of [...result.successfulFiles, ...result.failedFiles, ...result.unknownFiles]) {
    if (!entry || typeof entry.fileId !== 'string' || !expected.has(entry.fileId)
      || expected.get(entry.fileId) !== entry.filename || seen.has(entry.fileId)) {
      return false;
    }
    seen.add(entry.fileId);
  }
  if (seen.size !== expected.size || expected.size !== manifest.files.length) {
    return false;
  }
  if (result.successfulFiles.some((entry) => !isCount(entry.importedArticleCount) || !isCount(entry.skippedArticleCount))
    || result.failedFiles.some((entry) => typeof entry.retryable !== 'boolean')
    || [...result.failedFiles, ...result.unknownFiles].some((entry) => typeof entry.errorCode !== 'string' || typeof entry.errorMessage !== 'string')) {
    return false;
  }
  const successes = new Set(result.successfulFiles.map((entry) => entry.fileId));
  if (result.warningFiles.some((entry) => !entry || !successes.has(entry.fileId)
    || expected.get(entry.fileId) !== entry.filename || typeof entry.warningCode !== 'string' || typeof entry.warningMessage !== 'string')) {
    return false;
  }
  // 总数必须能够由逐文件证据重建，避免重试时累加错误或响应截断。
  const counts = [result.importedArticleCount, result.skippedArticleCount, result.failedFileCount, result.retryableFileCount, result.unknownFileCount];
  return counts.every(isCount)
    && result.importedArticleCount === result.successfulFiles.reduce((sum, entry) => sum + entry.importedArticleCount, 0)
    && result.skippedArticleCount === result.successfulFiles.reduce((sum, entry) => sum + entry.skippedArticleCount, 0)
    && result.failedFileCount === result.failedFiles.length
    && result.retryableFileCount === result.failedFiles.filter((entry) => entry.retryable).length
    && result.unknownFileCount === result.unknownFiles.length;
}

/**
 * 按 fileId 替换本次文件结果，再从全部保留证据重新聚合；不累加旧汇总。
 * @param {ArticleImportViewResult | undefined} previous 之前已确认的页面结果，首次提交为空。
 * @param {unknown} response 本次最终响应；网络异常或缺失时传入 undefined。
 * @param {ArticleImportManifest} manifest 本次真正上传的文件，重试仅包含可重试失败项。
 * @param {readonly ArticleImportFile[]} allFiles 不可变的原始完整选择顺序，重试时仍传全部文件。
 * @returns {ArticleImportViewResult} 保留其他文件结果的最新汇总；无完整响应不推测文章数量。
 */
export function mergeImportResult(
  previous: ArticleImportViewResult | undefined,
  response: unknown,
  manifest: ArticleImportManifest,
  allFiles: readonly ArticleImportFile[]
): ArticleImportViewResult {
  const selectionOrder = new Map(allFiles.map((entry, index) => [entry.fileId, index]));
  /**
   * 按首次选择顺序排列结果，不因重试或结果分类变化而改变文件位置。
   * @param {{fileId: string}} left 左侧文件结果。
   * @param {{fileId: string}} right 右侧文件结果。
   * @returns {number} 两个文件在完整选择清单中的位置差。
   */
  function compareFileOrder(left: { fileId: string }, right: { fileId: string }): number {
    return (selectionOrder.get(left.fileId) ?? allFiles.length) - (selectionOrder.get(right.fileId) ?? allFiles.length);
  }
  const complete = isCompleteImportResult(response, manifest);
  const replacedIds = new Set(manifest.files.map((entry) => entry.fileId));
  const successfulFiles = [
    ...(previous?.successfulFiles || []).filter((entry) => !replacedIds.has(entry.fileId)),
    ...(complete ? response.successfulFiles : []),
  ];
  const failedFiles = [
    ...(previous?.failedFiles || []).filter((entry) => !replacedIds.has(entry.fileId)),
    ...(complete ? response.failedFiles : []),
  ];
  const warningFiles = [
    ...(previous?.warningFiles || []).filter((entry) => !replacedIds.has(entry.fileId)),
    ...(complete ? response.warningFiles : []),
  ];
  const unknownFiles = [
    ...(previous?.unknownFiles || []).filter((entry) => !replacedIds.has(entry.fileId)),
    ...(complete ? response.unknownFiles : manifest.files.map(({ fileId, filename }) => ({
      fileId, filename, errorCode: 'RESULT_UNCONFIRMED', errorMessage: '导入结果未确认',
    }))),
  ];
  // 仅排序新构建的结果数组，原始选择及上一轮证据保持不变。
  successfulFiles.sort(compareFileOrder);
  failedFiles.sort(compareFileOrder);
  warningFiles.sort(compareFileOrder);
  unknownFiles.sort(compareFileOrder);
  // 完整响应确认的0篇同样是有效证据，后续某轮断网不能抹去该确认依据。
  const countsKnown = complete || previous?.countsKnown === true || successfulFiles.length > 0 || unknownFiles.length === 0;
  return {
    batchId: manifest.batchId,
    countsKnown,
    importedArticleCount: countsKnown ? successfulFiles.reduce((sum, entry) => sum + entry.importedArticleCount, 0) : undefined,
    skippedArticleCount: countsKnown ? successfulFiles.reduce((sum, entry) => sum + entry.skippedArticleCount, 0) : undefined,
    failedFileCount: failedFiles.length,
    retryableFileCount: failedFiles.filter((entry) => entry.retryable).length,
    unknownFileCount: unknownFiles.length,
    successfulFiles, failedFiles, warningFiles, unknownFiles,
  };
}

/**
 * 按已确认结果生成汇总，不把待核实项并入失败或推算文章数。
 * @param {ArticleImportViewResult} result 当前文件集合的本地聚合结果。
 * @returns {string} 安全汇总文案。
 */
export function getImportSummary(result: ArticleImportViewResult): string {
  if (!result.countsKnown) {
    return '导入结果未确认，请核实文章列表后再操作';
  }
  const imported = result.importedArticleCount ?? 0;
  const failed = result.failedFileCount ?? 0;
  const unknown = result.unknownFileCount ?? 0;
  if (unknown > 0) {
    return '已确认成功导入 ' + imported + ' 篇文章，失败 ' + failed + ' 个文件，另有 ' + unknown + ' 个文件结果待核实';
  }
  if (!failed) {
    return '本次成功导入 ' + imported + ' 篇文章';
  }
  if (imported > 0) {
    return '成功导入 ' + imported + ' 篇文章，另有 ' + failed + ' 个文件导入失败';
  }
  return '本次未能成功导入文章，' + failed + ' 个文件导入失败';
}
